import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Chip, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

export interface EntityCardStat {
  label: string;
  value: number | string;
}

interface EntityCardProps {
  name: string;
  type: string;
  icon: IconProp;
  color: string;
  onClick: () => void;
  statusLabel?: string;
  count?: number;
  description?: string;
  stats?: EntityCardStat[];
  createdDate?: string;
  lastUpdatedDate?: string;
}

const EntityCard = ({ name, type, icon, color, onClick, statusLabel, description, stats, createdDate, lastUpdatedDate }: EntityCardProps) => {
  const theme = useTheme();

  const displayDate = lastUpdatedDate || createdDate;
  const dateLabel = lastUpdatedDate ? 'Updated' : 'Created';

  const formattedDate = displayDate
    ? `${dateLabel}: ${new Date(displayDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    : null;

  return (
    <Box
      sx={{
        width: { xs: '100%', sm: 280 },
        borderRadius: '18px',
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          borderColor: color,
          bgcolor: alpha(color, 0.02),
          boxShadow: `0 8px 24px ${alpha(color, 0.12)}`,
          transform: 'translateY(-3px)',
          '& .entity-icon-box': {
            bgcolor: color,
            color: '#fff',
            boxShadow: `0 4px 14px ${alpha(color, 0.35)}`,
          },
          '& .open-btn': {
            bgcolor: color,
            color: '#fff',
            borderColor: color,
          },
        },
      }}
    >
      {/* Top section with icon and info */}
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 1.5,
          cursor: 'pointer',
        }}
        onClick={onClick}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          {/* Icon */}
          <Box
            className="entity-icon-box"
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              bgcolor: alpha(color, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color,
              flexShrink: 0,
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <FontAwesomeIcon icon={icon} style={{ fontSize: 20 }} />
          </Box>

          {/* Name + type + status */}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Tooltip title={name} placement="top" arrow>
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: 'text.primary',
                    fontSize: '14px',
                    lineHeight: 1.3,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {name}
                </Typography>
              </Tooltip>
              {statusLabel && (
                <FiberManualRecordIcon
                  sx={{
                    fontSize: 8,
                    color: statusLabel === 'active' ? theme.palette.success.main : theme.palette.warning.main,
                    flexShrink: 0,
                  }}
                />
              )}
            </Stack>
            <Typography
              sx={{
                fontWeight: 600,
                color: color,
                fontSize: '10px',
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                opacity: 0.85,
                mt: 0.25,
              }}
            >
              {type}
            </Typography>
            {description && (
              <Typography
                sx={{
                  fontWeight: 500,
                  color: 'text.secondary',
                  fontSize: '11px',
                  lineHeight: 1.4,
                  mt: 0.75,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {description}
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>

      {/* Stats chips */}
      {stats && stats.length > 0 && (
        <Box
          sx={{
            px: 2,
            pb: 1,
            cursor: 'pointer',
          }}
          onClick={onClick}
        >
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap rowGap={0.5}>
            {stats.map((stat, i) => (
              <Chip
                key={i}
                label={
                  <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: 0.2 }}>
                    <Box component="span" sx={{ color: 'text.primary' }}>
                      {stat.value}
                    </Box>
                    <Box component="span" sx={{ color: 'text.secondary', ml: 0.4 }}>
                      {stat.label}
                    </Box>
                  </Typography>
                }
                size="small"
                sx={{
                  height: 22,
                  borderRadius: '6px',
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  border: '1px solid',
                  borderColor: alpha(theme.palette.divider, 0.6),
                  '& .MuiChip-label': { px: 0.75, py: 0 },
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Footer with date and open button */}
      <Box
        sx={{
          px: 2,
          py: 1,
          mt: 'auto',
          borderTop: 1,
          borderColor: alpha(theme.palette.divider, 0.5),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: alpha(theme.palette.primary.main, 0.015),
        }}
      >
        {formattedDate ? (
          <Typography sx={{ fontSize: '10px', fontWeight: 500, color: 'text.disabled' }}>
            {formattedDate}
          </Typography>
        ) : (
          <Box />
        )}
        <Button
          className="open-btn"
          size="small"
          variant="outlined"
          onClick={onClick}
          endIcon={<OpenInNewIcon sx={{ fontSize: '13px !important' }} />}
          sx={{
            textTransform: 'none',
            fontSize: '11px',
            fontWeight: 700,
            borderRadius: '8px',
            px: 1.5,
            py: 0.25,
            minHeight: 0,
            borderColor: alpha(color, 0.35),
            color: color,
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              bgcolor: color,
              color: '#fff',
              borderColor: color,
            },
          }}
        >
          Open
        </Button>
      </Box>
    </Box>
  );
};

export default EntityCard;
